import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

interface WebSocketMessage {
  type: string;
  job_id?: string;
  data: any;
}

interface UseWebSocketOptions {
  enabled?: boolean;
  onMessage?: (message: WebSocketMessage) => void;
  onConnectionChange?: (connected: boolean) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

interface WebSocketState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  reconnectAttempts: number;
  lastConnectedAt: Date | null;
  lastError: string | null;
}

// Token cache to avoid fetching on every reconnect
interface TokenCache {
  token: string;
  userId: string;
  expiresAt: number;
}

let tokenCache: TokenCache | null = null;

/**
 * Fetch a WebSocket authentication token from the backend.
 * Uses cookie-based auth to get a short-lived token for WebSocket.
 */
async function fetchWebSocketToken(backendUrl: string): Promise<{ token: string; userId: string } | null> {
  // Check cache first (with 30 second buffer before expiry)
  if (tokenCache && tokenCache.expiresAt > Date.now() + 30000) {
    console.log('🔑 Using cached WebSocket token');
    return { token: tokenCache.token, userId: tokenCache.userId };
  }

  try {
    console.log('🔑 Fetching new WebSocket token...');
    const response = await fetch(`${backendUrl}/auth/ws-token`, {
      method: 'GET',
      credentials: 'include', // Send cookies for authentication
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to fetch WebSocket token:', response.status, errorText);
      return null;
    }

    const data = await response.json();

    // Cache the token
    tokenCache = {
      token: data.token,
      userId: data.user_id,
      expiresAt: Date.now() + (data.expires_in * 1000) // Convert seconds to ms
    };

    console.log('✅ WebSocket token obtained, expires in', data.expires_in, 'seconds');
    return { token: data.token, userId: data.user_id };
  } catch (error) {
    console.error('❌ Error fetching WebSocket token:', error);
    return null;
  }
}

/**
 * Clear the cached WebSocket token (call on logout)
 */
export function clearWebSocketTokenCache(): void {
  tokenCache = null;
  console.log('🔑 WebSocket token cache cleared');
}

/**
 * Calculate exponential backoff delay with jitter
 */
function getBackoffDelay(attempt: number, baseDelay: number = 1000, maxDelay: number = 30000): number {
  const exponentialDelay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  // Add random jitter (±25%)
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
  return Math.round(exponentialDelay + jitter);
}

/**
 * Nuclear-grade WebSocket hook for real-time job updates.
 *
 * Features:
 * - Automatic token fetching (no cookie issues)
 * - Exponential backoff with jitter for reconnection
 * - Connection health monitoring with ping/pong
 * - Automatic token refresh before expiry
 * - Comprehensive error handling and logging
 * - State management for UI feedback
 */
export const useWebSocket = ({
  enabled = true,
  onMessage,
  onConnectionChange,
  reconnectInterval = 5000,
  maxReconnectAttempts = 10
}: UseWebSocketOptions = {}) => {
  const { user } = useAuth();
  const [state, setState] = useState<WebSocketState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    reconnectAttempts: 0,
    lastConnectedAt: null,
    lastError: null
  });

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const tokenRefreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const mountedRef = useRef(true);
  const connectingRef = useRef(false);

  /**
   * Update state safely (only if component is mounted)
   */
  const safeSetState = useCallback((updates: Partial<WebSocketState>) => {
    if (mountedRef.current) {
      setState(prev => ({ ...prev, ...updates }));
    }
  }, []);

  /**
   * Clean up all timers and the WebSocket connection
   */
  const cleanup = useCallback(() => {
    console.log('🧹 Cleaning up WebSocket resources');

    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
      tokenRefreshTimeoutRef.current = null;
    }

    if (wsRef.current) {
      // Remove event handlers to prevent callbacks after cleanup
      wsRef.current.onopen = null;
      wsRef.current.onmessage = null;
      wsRef.current.onerror = null;
      wsRef.current.onclose = null;

      if (wsRef.current.readyState === WebSocket.OPEN ||
          wsRef.current.readyState === WebSocket.CONNECTING) {
        wsRef.current.close(1000, 'Client cleanup');
      }
      wsRef.current = null;
    }

    connectingRef.current = false;
  }, []);

  /**
   * Schedule token refresh before it expires
   */
  const scheduleTokenRefresh = useCallback((expiresIn: number) => {
    if (tokenRefreshTimeoutRef.current) {
      clearTimeout(tokenRefreshTimeoutRef.current);
    }

    // Refresh 60 seconds before expiry
    const refreshIn = Math.max((expiresIn - 60) * 1000, 30000);
    console.log(`⏰ Token refresh scheduled in ${Math.round(refreshIn / 1000)} seconds`);

    tokenRefreshTimeoutRef.current = setTimeout(() => {
      console.log('🔄 Token expiring soon, refreshing connection...');
      // Clear the cached token to force a new fetch
      tokenCache = null;
      // Reconnect with new token
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.close(1000, 'Token refresh');
      }
    }, refreshIn);
  }, []);

  /**
   * Connect to WebSocket with token authentication
   */
  const connect = useCallback(async () => {
    // Prevent multiple simultaneous connection attempts
    if (connectingRef.current) {
      console.log('⚠️ Connection already in progress, skipping');
      return;
    }

    if (!user || !enabled) {
      console.log('⚠️ Cannot connect: user not authenticated or WebSocket disabled');
      return;
    }

    connectingRef.current = true;
    safeSetState({ isConnecting: true, error: null });

    try {
      // Get backend URL
      const backendUrl = import.meta.env.VITE_BACKEND_URL || `${window.location.origin}/api/v1`;

      // Step 1: Fetch authentication token
      const tokenData = await fetchWebSocketToken(backendUrl);

      if (!tokenData) {
        throw new Error('Failed to obtain WebSocket authentication token');
      }

      // Verify user ID matches
      if (tokenData.userId !== user.id) {
        console.warn('⚠️ Token user ID mismatch, clearing cache');
        tokenCache = null;
        throw new Error('Authentication mismatch');
      }

      // Step 2: Build WebSocket URL with token
      const wsBaseUrl = backendUrl.replace(/^http/, 'ws');
      const wsUrl = `${wsBaseUrl}/ws/${user.id}?token=${encodeURIComponent(tokenData.token)}`;

      console.log('🔌 Connecting to WebSocket:', wsUrl.replace(/token=.*/, 'token=***'));

      // Step 3: Create WebSocket connection
      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        if (!mountedRef.current) return;

        console.log('✅ WebSocket connected successfully');
        connectingRef.current = false;

        safeSetState({
          isConnected: true,
          isConnecting: false,
          error: null,
          reconnectAttempts: 0,
          lastConnectedAt: new Date()
        });

        onConnectionChange?.(true);

        // Start ping interval to keep connection alive
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send('ping');
          }
        }, 25000); // Ping every 25 seconds

        // Schedule token refresh
        if (tokenCache) {
          const expiresIn = Math.floor((tokenCache.expiresAt - Date.now()) / 1000);
          scheduleTokenRefresh(expiresIn);
        }
      };

      ws.onmessage = (event) => {
        if (!mountedRef.current) return;

        try {
          // Handle pong response
          if (event.data === 'pong') {
            return;
          }

          // Parse JSON message
          const message: WebSocketMessage = JSON.parse(event.data);
          console.log('📨 WebSocket message:', message.type, message.job_id || '');

          onMessage?.(message);
        } catch (err) {
          console.error('❌ Failed to parse WebSocket message:', err);
        }
      };

      ws.onerror = (event) => {
        console.error('❌ WebSocket error event:', event);
        // Don't set error state here, let onclose handle it
      };

      ws.onclose = (event) => {
        if (!mountedRef.current) return;

        console.log('🔌 WebSocket closed:', event.code, event.reason);
        connectingRef.current = false;

        // Clean up ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
          pingIntervalRef.current = null;
        }

        const wasConnected = state.isConnected;
        safeSetState({
          isConnected: false,
          isConnecting: false
        });

        if (wasConnected) {
          onConnectionChange?.(false);
        }

        // Handle different close codes
        const shouldReconnect = enabled && user && event.code !== 1000;

        if (event.code === 1008) {
          // Policy violation (authentication failed)
          console.error('❌ WebSocket authentication failed');
          tokenCache = null; // Clear cache to force new token
          safeSetState({
            error: 'Authentication failed',
            lastError: 'Authentication failed - will retry with new token'
          });
        } else if (event.code === 1011) {
          // Internal server error
          safeSetState({
            error: 'Server error',
            lastError: 'Server error - will retry'
          });
        }

        // Attempt reconnection with exponential backoff
        if (shouldReconnect && state.reconnectAttempts < maxReconnectAttempts) {
          const delay = getBackoffDelay(state.reconnectAttempts, reconnectInterval);
          console.log(`🔄 Reconnecting in ${delay}ms (attempt ${state.reconnectAttempts + 1}/${maxReconnectAttempts})`);

          safeSetState({ reconnectAttempts: state.reconnectAttempts + 1 });

          reconnectTimeoutRef.current = setTimeout(() => {
            if (mountedRef.current && enabled) {
              connect();
            }
          }, delay);
        } else if (state.reconnectAttempts >= maxReconnectAttempts) {
          console.error('❌ Max reconnection attempts reached');
          safeSetState({
            error: 'Connection failed after multiple attempts',
            lastError: `Failed after ${maxReconnectAttempts} attempts`
          });
        }
      };

      wsRef.current = ws;

    } catch (err) {
      console.error('❌ WebSocket connection error:', err);
      connectingRef.current = false;

      const errorMessage = err instanceof Error ? err.message : 'Connection failed';
      safeSetState({
        isConnecting: false,
        error: errorMessage,
        lastError: errorMessage
      });

      // Retry connection
      if (enabled && user && state.reconnectAttempts < maxReconnectAttempts) {
        const delay = getBackoffDelay(state.reconnectAttempts, reconnectInterval);
        console.log(`🔄 Retrying connection in ${delay}ms`);

        safeSetState({ reconnectAttempts: state.reconnectAttempts + 1 });

        reconnectTimeoutRef.current = setTimeout(() => {
          if (mountedRef.current && enabled) {
            connect();
          }
        }, delay);
      }
    }
  }, [user, enabled, onMessage, onConnectionChange, reconnectInterval, maxReconnectAttempts, state.isConnected, state.reconnectAttempts, safeSetState, scheduleTokenRefresh]);

  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    console.log('🔌 Disconnecting WebSocket');
    cleanup();
    safeSetState({
      isConnected: false,
      isConnecting: false,
      reconnectAttempts: 0
    });
  }, [cleanup, safeSetState]);

  /**
   * Force reconnect (useful for manual retry)
   */
  const reconnect = useCallback(() => {
    console.log('🔄 Force reconnecting...');
    cleanup();
    safeSetState({ reconnectAttempts: 0 });
    // Small delay to ensure cleanup completes
    setTimeout(() => {
      if (mountedRef.current && enabled && user) {
        connect();
      }
    }, 100);
  }, [cleanup, connect, enabled, user, safeSetState]);

  /**
   * Send message to WebSocket
   */
  const send = useCallback((data: any) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
      return true;
    } else {
      console.warn('⚠️ Cannot send message: WebSocket not connected');
      return false;
    }
  }, []);

  /**
   * Connect/disconnect based on enabled state and user
   */
  useEffect(() => {
    mountedRef.current = true;

    if (enabled && user) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [enabled, user?.id]); // Only depend on user.id, not the full user object

  return {
    ...state,
    send,
    reconnect,
    disconnect
  };
};

export default useWebSocket;
